import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { WHATSAPP_URL } from "../lib/constants";
import { useT } from "../i18n";

export const PorQueEscolher = () => {
  const { t } = useT();
  const PILLARS = t("porque.pillars");

  return (
    <section
      id="por-que-escolher"
      data-testid="why-choose-section"
      className="relative py-24 md:py-40 bg-[#FEFEFE]"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-6">
        {/* ═══ Header (left-aligned) ═══ */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl"
        >
          <div className="flex items-center gap-3 mb-7">
            <span className="h-px w-10 bg-[#A3BD98]" />
            <span className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#ADBDAD]">
              {t("porque.eyebrow")}
            </span>
          </div>

          <h2 className="font-serif text-[34px] md:text-[44px] lg:text-[54px] leading-[1.1] tracking-tight text-[#2A322C] font-semibold">
            {t("porque.titleStart")}
            <span className="font-semibold text-[#2A322C]">
              {t("porque.titleBrand")}
            </span>
          </h2>

          <p className="mt-7 md:mt-8 text-[17px] md:text-[19px] text-[#2A322C] leading-[1.65] max-w-[660px] font-light">
            {t("porque.lead")}
          </p>

          <p className="mt-5 text-[15px] md:text-[16px] text-[#5C6660] leading-[1.85] max-w-[600px]">
            {t("porque.body")}
          </p>
        </motion.div>

        {/* ═══ Grid of pillars ═══ */}
        <div className="mt-20 md:mt-28 grid grid-cols-1 md:grid-cols-2 gap-x-12 md:gap-x-16 lg:gap-x-24 gap-y-0">
          {PILLARS.map((p, i) => (
            <motion.div
              key={p.num}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{
                duration: 0.6,
                delay: i * 0.08,
                ease: "easeOut",
              }}
              data-testid={`pillar-${p.num}`}
              className={`group relative py-10 md:py-12 border-b border-[#E4E9DE]/70 transition-all duration-300 ${
                i === PILLARS.length - 1 ? "border-b-0 md:border-b-0" : ""
              } ${i === PILLARS.length - 2 ? "md:border-b-0" : ""}`}
            >
              <div className="flex items-start gap-6 md:gap-8">
                {/* Number */}
                <div className="shrink-0 flex items-baseline">
                  <span
                    className="font-serif text-[56px] md:text-[72px] leading-none font-light tracking-tight text-transparent transition-colors duration-500 group-hover:text-[#A3BD98]/80"
                    style={{
                      WebkitTextStroke: "1px #A3BD98",
                    }}
                  >
                    {p.num}
                  </span>
                </div>

                {/* Content */}
                <div className="flex-1 pt-2">
                  <h3 className="font-serif text-[20px] md:text-[22px] font-semibold text-[#2A322C] leading-[1.3] tracking-tight">
                    {p.title}
                  </h3>
                  <p className="mt-4 text-[15px] md:text-[15.5px] text-[#5C6660] leading-[1.8] max-w-md">
                    {p.text}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* ═══ CTA ═══ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-20 md:mt-28 flex flex-col items-center gap-5 text-center"
        >
          <p className="text-[13.5px] uppercase tracking-[0.28em] text-[#ADBDAD] font-semibold">
            {t("porque.ctaEyebrow")}
          </p>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="pillars-cta-btn"
            className="group/btn inline-flex items-center gap-2 bg-[#EE8984] text-white rounded-full px-8 py-4 text-sm font-semibold hover:bg-[#CF8376] transition-colors"
          >
            {t("porque.ctaButton")}
            <ArrowUpRight
              size={17}
              className="group-hover/btn:rotate-45 transition-transform duration-300"
            />
          </a>
        </motion.div>
      </div>
    </section>
  );
};
